import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import { UI_URL } from '../../../Settings';
import GameNameEditable from '../Game/GameNameEditable';
import GameConfig from './GameConfig';
import PeerLeftModal from './PeerLeftModal';
import SeatRow from './SeatRow';
// CC_LOBBY_OVERLAY_WINDOW (2026-06-07): SEAT_COUNT + layoutFromLobbyData
// moved to the shared seatLayout module so the in-game Lobby overlay
// renders the identical roster. The forming render below still calls
// layoutFromLobbyData, so it is pixel-identical to before the move.
import { SEAT_COUNT, layoutFromLobbyData } from './seatLayout';

import './lobby.css';

const COUNTDOWN_SECONDS = 5;
const PLAYER_ONE_DEFAULT_SEAT = 0;
const SINGLE_PLAYER_PARAM = 'single';
// CC_LOBBY_GAME_CRUD (2026-05-27): matches LobbyStatus.PLAYING_ID in
// hsh_python/lobbies/models.py. Don't hardcode the integer at call
// sites — the constant exists so a future renumber is one-line.
const LOBBY_STATUS_PLAYING = 3;
// Lobby background art (Hexway Boogie Woogie). Lives in
// game_static/ui/ like every other UI asset; lobby.css
// `.lobby-background-image` carries the cover/bleed treatment.
const LOBBY_BACKGROUND_URL = `${UI_URL}HexwayBoogieWoogiev1.png`;


function _readyTally(layout) {
    // CC_LOBBY §5.6 + Phase C migration repair (2026-05-21): AIs are
    // implicit-Ready, Closed slots count as Ready for gating, only Human
    // rows whose occupant.ready === true count toward the Start gate.
    // `occupant.ready` is sourced from the live Seat.ready (via
    // _layoutFromLobbyData), not the dead options.ready dual-write.
    let humans = 0;
    let humansReady = 0;
    let everyDecided = true;
    layout.forEach((slot) => {
        if (slot.kind === 'open') {
            everyDecided = false;
        }
        if (slot.kind === 'human') {
            humans += 1;
            if (slot.occupant && slot.occupant.ready) {
                humansReady += 1;
            }
        }
    });
    let allHumansReady = humans === 0 || humansReady === humans;
    return { humans, humansReady, everyDecided, allHumansReady };
}


function _computeSeatLayout(members, owner, ais) {
    // Returns an array of length SEAT_COUNT; each slot is one of:
    //   { kind: 'human', occupant: <member> }
    //   { kind: 'ai',    aiIndex: <int> }
    //   { kind: 'open' }
    //
    // Owner anchors seat 0. Other humans fill remaining open rows in
    // members-array order. AIs fill remaining open rows in
    // owner.options.ai[] order.
    //
    // CC_CLOSED_SEAT_BROADCAST: this is the pre-broadcast fallback only
    // (no seats[] payload has arrived yet). Closed seats are now
    // server-owned state carried on the Seat broadcast, so they cannot
    // be known here — they render once _layoutFromLobbyData takes over
    // on the first Members push. No client-local closed Set exists.
    const layout = new Array(SEAT_COUNT).fill(null);
    const ownerId = owner ? owner.id : null;
    if (owner) {
        layout[PLAYER_ONE_DEFAULT_SEAT] = {
            kind: 'human',
            occupant: owner,
        };
    }
    let nextSeat = 0;
    const claim = (slot) => {
        while (nextSeat < SEAT_COUNT && layout[nextSeat] !== null) {
            nextSeat += 1;
        }
        if (nextSeat < SEAT_COUNT) {
            layout[nextSeat] = slot;
            nextSeat += 1;
        }
    };
    if (members) {
        members.forEach((member) => {
            if (!member || member.id === ownerId) return;
            claim({ kind: 'human', occupant: member });
        });
    }
    (ais || []).forEach((_aiEntry, aiIndex) => {
        claim({ kind: 'ai', aiIndex });
    });
    for (let i = 0; i < SEAT_COUNT; i += 1) {
        if (layout[i] === null) {
            layout[i] = { kind: 'open' };
        }
    }
    return layout;
}


export default function Lobby(props) {
    const { app } = props;
    const [searchParams] = useSearchParams();
    // CC_CLOSED_SEAT_BROADCAST: closed seats are server-owned and read
    // off the Seat broadcast (seat.closed), not a client-local Set. The
    // old `useState(new Set())` + its closeSeat/reopenSeat Set mutations
    // are gone — closing now sends a CloseSeat message and the seat
    // re-renders from the resulting Members broadcast like every other
    // lobby mutation.
    const [phase, setPhase] = useState('idle');
    const [countdown, setCountdown] = useState(null);
    const [launched, setLaunched] = useState(false);
    // CC_PLAYER_ONE_TRANSFER: owner-local designation. Persists only
    // until the owner navigates away or reloads — engine cements at
    // launch time (Manual §3, "the most recently haunted player
    // decides who goes first").
    const [designatedSeat, setDesignatedSeat] = useState(
        PLAYER_ONE_DEFAULT_SEAT);
    const designatedSeatRef = useRef(designatedSeat);
    useEffect(() => {
        designatedSeatRef.current = designatedSeat;
    }, [designatedSeat]);
    const singlePlayerFiredRef = useRef(false);

    // Group state: props are the live snapshot from a parent that
    // already listens (PlayMenu does this). Fall back to the
    // groupHandler's last known values when the parent doesn't
    // pipe them (GameComponent). Either way, refresh from
    // group-update-event so the Lobby tracks live changes itself
    // and doesn't depend on the parent's plumbing.
    const initialMembers = (
        props.members
        || (app && app.game && app.game.groupHandler
            && app.game.groupHandler.groupMembers)
        || []);
    const initialMember = (
        props.member
        || (app && app.game && app.game.groupHandler
            && app.game.groupHandler.memberRecord)
        || null);
    // Phase C migration repair (2026-05-21): owner identity is the
    // Lobby's owning User, surfaced as owner_user_id on every Members
    // broadcast and stashed on groupHandler._ownerUserId. The dead
    // `m.group === m.id` self-FK lookup is gone.
    const initialOwnerUserId = (
        (typeof props.ownerUserId === 'number' ? props.ownerUserId : null)
        || (app && app.game && app.game.groupHandler
            && app.game.groupHandler._ownerUserId)
        || null);
    const initialOwner = props.owner
        || (initialOwnerUserId
            ? (initialMembers.find(
                (m) => m && m.user && m.user.id === initialOwnerUserId)
                || initialMember)
            : initialMember);
    const initialIsGroupLeader = (
        typeof props.isGroupLeader === 'boolean'
            ? props.isGroupLeader
            : Boolean(initialMember && initialMember.user
                && initialOwnerUserId !== null
                && initialOwnerUserId !== undefined
                && initialMember.user.id === initialOwnerUserId));
    const [groupState, setGroupState] = useState({
        members: initialMembers,
        member: initialMember,
        owner: initialOwner,
        isGroupLeader: initialIsGroupLeader,
        ownerUserId: initialOwnerUserId,
    });

    // DiscordProvider fires `discord-lobby-joined` after the
    // /api/lobby/from_platform/ POST completes. The WebSocket Members
    // broadcast (group-update-event below) also seeds seats here for
    // the open-web path. Either way `_layoutFromLobbyData` becomes the
    // default once seats[] has shipped at least once.
    //
    // CC_DISCORD_LOBBY_SEED_NULL (2026-06-06): lobbyData is NOT seeded
    // from `discord.lobby`. That context field is set only by the
    // test-only DiscordProvider.dispatchLobbyJoined — the production menu
    // helpers fire the `discord-lobby-joined` window event (handled below)
    // but never stash to context, so `discord.lobby` is always null in
    // prod. The event + the Members broadcast (group-update-event) are the
    // live seed sources; the original "stash survives navigation timing"
    // backup was never wired and removing the dead read keeps a single
    // owner of lobby context.
    // Mount-time seed order: groupHandler cache (covers the postgame race
    // where the Members broadcast that flipped GamePage to the lobby
    // surface fired before this component mounted) → null (legacy
    // _computeSeatLayout fallback until the first live broadcast).
    const [lobbyData, setLobbyData] = useState(() => {
        const handler = (app && app.game && app.game.groupHandler)
            || null;
        const cachedSeats = (handler && handler.groupSeats) || [];
        const cachedConfig = (handler && handler.lobbyConfig) || null;
        if (cachedSeats.length === 0 && !cachedConfig) return null;
        return {
            ...(cachedConfig || {}),
            ...(cachedSeats.length > 0 ? { seats: cachedSeats } : {}),
        };
    });

    useEffect(() => {
        const handleGroupUpdate = (event) => {
            const detail = event.detail || {};
            setGroupState({
                members: detail.members || [],
                member: detail.member || null,
                owner: detail.owner || null,
                isGroupLeader: Boolean(detail.isGroupLeader),
                ownerUserId: (detail.ownerUserId !== undefined
                    ? detail.ownerUserId : null),
            });
            const seats = detail.seats;
            const cfg = detail.lobbyConfig || {};
            const hasSeats = seats && seats.length > 0;
            const hasCfg = (
                typeof cfg.amenities === 'number'
                || typeof cfg.beds === 'number'
                || typeof cfg.debris === 'number'
                || typeof cfg.ghosts === 'number'
                || typeof cfg.deck === 'number'
                || typeof cfg.public === 'boolean'
                || Array.isArray(cfg.expansions));
            if (hasSeats || hasCfg) {
                setLobbyData((prev) => ({
                    ...(prev || {}),
                    ...(hasSeats ? { seats } : {}),
                    ...(typeof cfg.amenities === 'number'
                        ? { amenities: cfg.amenities } : {}),
                    ...(typeof cfg.beds === 'number'
                        ? { beds: cfg.beds } : {}),
                    ...(typeof cfg.debris === 'number'
                        ? { debris: cfg.debris } : {}),
                    ...(typeof cfg.ghosts === 'number'
                        ? { ghosts: cfg.ghosts } : {}),
                    ...(typeof cfg.deck === 'number'
                        ? { deck: cfg.deck } : {}),
                    ...(Array.isArray(cfg.expansions)
                        ? { expansions: cfg.expansions } : {}),
                    // CC_PUBLIC_TOGGLE_ROUNDTRIP (2026-06-08): pass the
                    // public flag through on the SAME footing as
                    // amenities/beds. The Members/Join broadcast already
                    // carries it (group_router) and receiveGroupMembers
                    // surfaces lobbyConfig.public — but this merge dropped
                    // it, so the owner's toggle persisted server-side yet
                    // never repainted the ✅. Gated on boolean so an
                    // undefined (legacy payload) never clobbers a good
                    // value.
                    ...(typeof cfg.public === 'boolean'
                        ? { public: cfg.public } : {}),
                    // CC_LOBBY_GAME_CRUD: Leave Game / Abandon gating.
                    ...(typeof cfg.status_id === 'number'
                        ? { status_id: cfg.status_id } : {}),
                    ...(cfg.current_game_id !== undefined
                        ? { current_game_id: cfg.current_game_id } : {}),
                }));
            }
        };
        window.addEventListener('group-update-event', handleGroupUpdate);
        return () => window.removeEventListener(
            'group-update-event', handleGroupUpdate);
    }, []);

    useEffect(() => {
        const handleLobbyJoined = (event) => {
            const detail = event.detail || {};
            setLobbyData(detail.lobby || null);
        };
        window.addEventListener(
            'discord-lobby-joined', handleLobbyJoined);
        return () => window.removeEventListener(
            'discord-lobby-joined', handleLobbyJoined);
    }, []);

    // Sync from props on changes — useful when PlayMenu pipes live
    // values via its own listener.
    useEffect(() => {
        if (props.members === undefined && props.member === undefined
                && props.owner === undefined) {
            return;
        }
        setGroupState((prev) => ({
            members: props.members !== undefined
                ? props.members : prev.members,
            member: props.member !== undefined
                ? props.member : prev.member,
            owner: props.owner !== undefined
                ? props.owner : prev.owner,
            isGroupLeader: typeof props.isGroupLeader === 'boolean'
                ? props.isGroupLeader : prev.isGroupLeader,
            // CC_SEAT_IDENTITY_MARKS: preserve ownerUserId across a
            // props-only sync (no ownerUserId prop) so the owner-seat
            // mark gate keeps a live owner id instead of dropping to
            // undefined. The owner-row match reads this.
            ownerUserId: prev.ownerUserId,
        }));
    }, [
        props.members, props.member, props.owner, props.isGroupLeader,
    ]);

    const {
        members, member, owner, isGroupLeader, ownerUserId,
    } = groupState;

    // ---- Single-player auto-fill on mount ----
    useEffect(() => {
        if (singlePlayerFiredRef.current) return;
        if (!isGroupLeader) return;
        if (searchParams.get(SINGLE_PLAYER_PARAM) !== '1') return;
        if (!app || !app.game || !app.game.gameApi) return;
        // Phase C migration repair: AI seats are now Seat rows, not a
        // member options.ai[] dual-write. Read the current AI count
        // from lobbyData.seats[].
        const lobbySeats = (lobbyData && lobbyData.seats) || [];
        const existingAIs = lobbySeats.filter((s) => s && s.ai);
        if (existingAIs.length > 0) {
            singlePlayerFiredRef.current = true;
            return;
        }
        singlePlayerFiredRef.current = true;
        app.game.gameApi.triggerSinglePlayer();
    }, [app, isGroupLeader, lobbyData, searchParams]);

    // ---- Countdown driver ----
    // One setTimeout owns the launch decision (5s after Start). A
    // parallel setInterval ticks the displayed seconds for the user.
    // Cancel clears both. This shape stays stable under jest's fake
    // timers because the launch isn't a chained setState→useEffect
    // cascade — it's a single deterministic timeout from click time.
    useEffect(() => {
        if (phase !== 'counting') return undefined;
        setCountdown(COUNTDOWN_SECONDS);
        const launchTimer = setTimeout(() => {
            setPhase('launched');
            setLaunched(true);
            if (app && app.game && app.game.groupHandler) {
                app.game.groupHandler.launchGroupGame({
                    player_one_seat: designatedSeatRef.current,
                });
            }
        }, COUNTDOWN_SECONDS * 1000);
        const tickInterval = setInterval(() => {
            setCountdown((prev) =>
                prev === null ? null : Math.max(0, prev - 1));
        }, 1000);
        return () => {
            clearTimeout(launchTimer);
            clearInterval(tickInterval);
        };
    }, [phase, app]);

    // ---- Layout computation ----
    // Phase C migration repair: AI list is the AI Seats from lobbyData,
    // not the legacy owner.options.ai dual-write. `_computeSeatLayout`
    // only runs as a fallback before any seats[] broadcast has arrived;
    // pass the empty list there too — the layout falls through to
    // open slots and self-corrects on the next Members push.
    const ais = useMemo(() => {
        const lobbySeats = (lobbyData && lobbyData.seats) || [];
        return lobbySeats.filter((s) => s && s.ai);
    }, [lobbyData]);

    const seatLayout = useMemo(
        () => (lobbyData
            ? layoutFromLobbyData(lobbyData)
            : _computeSeatLayout(members, owner, ais)),
        [lobbyData, members, owner, ais]);

    // CC_PLAYER_ONE_TRANSFER §5.4: self-correct when the designated
    // seat is no longer occupied (host closed it or the human left
    // after designating). Engine has its own clamp safety net.
    useEffect(() => {
        if (designatedSeat === PLAYER_ONE_DEFAULT_SEAT) return;
        const slot = seatLayout[designatedSeat];
        if (!slot || slot.kind === 'open' || slot.kind === 'closed') {
            setDesignatedSeat(PLAYER_ONE_DEFAULT_SEAT);
        }
    }, [seatLayout, designatedSeat]);

    const tally = useMemo(() => _readyTally(seatLayout), [seatLayout]);

    // ---- Per-row callbacks ----
    // CC_CLOSED_SEAT_BROADCAST: closing/re-opening a seat is owner-only,
    // server-owned state. Send a CloseSeat message (owner-gated, like
    // addAI) and let the resulting Members broadcast repaint the seat on
    // every client — no local Set mutation.
    const closeSeat = useCallback((seatNumber) => {
        if (!isGroupLeader) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.setSeatClosed(seatNumber, true);
    }, [app, isGroupLeader]);

    const reopenSeat = useCallback((seatNumber) => {
        if (!isGroupLeader) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.setSeatClosed(seatNumber, false);
    }, [app, isGroupLeader]);

    const addAI = useCallback(() => {
        if (!isGroupLeader) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.addArtificialPlayer();
    }, [app, isGroupLeader]);

    const removeAIByIndex = useCallback((aiIndex) => {
        if (!isGroupLeader) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.removeArtificialPlayer({ index: aiIndex });
    }, [app, isGroupLeader]);

    const takeSeat = useCallback((seatNumber) => {
        window.dispatchEvent(
            new CustomEvent('take-seat-event',
                { detail: parseInt(seatNumber, 10) }));
    }, []);

    const toggleReady = useCallback(() => {
        if (!member) return;
        if (!app || !app.game || !app.game.gameApi) return;
        // Phase C migration repair: ready state lives on Seat, not on
        // UserConnection.options. Source the current value from the
        // local user's Seat row and send the opposite.
        const localUserId = member.user && member.user.id;
        const lobbySeats = (lobbyData && lobbyData.seats) || [];
        const mySeat = lobbySeats.find(
            (s) => s && s.user && s.user.id === localUserId);
        const currentReady = Boolean(mySeat && mySeat.ready);
        // `configureGroupOptions` is the legacy write transport — the
        // backend `configure_group` handler translates `ready` into a
        // Seat.ready update. Send only the field we are toggling; the
        // engine no longer overwrites the whole options blob.
        app.game.gameApi.configureGroupOptions({ ready: !currentReady });
    }, [app, member, lobbyData]);

    const startCountdown = useCallback(() => {
        if (phase === 'counting' || phase === 'launched') return;
        setPhase('counting');
    }, [phase]);

    const cancelCountdown = useCallback(() => {
        setPhase('idle');
        setCountdown(null);
    }, []);

    const handleLeaveLobby = useCallback(() => {
        // Goes through groupHandler.leaveGameGroup → gameApi.leaveGroup
        // → backend leave_group (group_router.py). Per the
        // DISCORD_KNOWLEDGE §6.5/§7 rewrite (CC_DISCORD_LOBBY_LIFECYCLE):
        // leaving clears the binding and lands on the Main Menu — the
        // backend no longer auto-rejoins anyone into a fresh lobby.
        // Owner-leave on a forming lobby destroys it and sends every
        // peer to the Main Menu too.
        //
        // 2026-05-31: route to '/' (Main Menu), NOT '/casual'. With the
        // auto-rehome gone, '/casual' would mount Lobby.js with no lobby
        // and trip the WS group auto-create — re-spawning the very
        // fresh-lobby-on-leave symptom this CC removes. Main Menu sends
        // no Group messages, so the binding stays clear. The navigator's
        // one-shot guard means '/' no longer fights a force-push.
        if (app && app.game && app.game.groupHandler) {
            app.game.groupHandler.leaveGameGroup();
        }
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    }, [app]);

    // CC_SAVED_GAMES_LIST (2026-05-27) / CC_FE_PAGES (2026-06-01): the
    // Saved Games picker moved to the `/saves` page (SavesPage). The
    // lobby's Saved Games button now ROUTES there instead of opening a
    // modal, so the local show/hide state and the modal mount are gone.

    // CC_LOBBY_GAME_CRUD (2026-05-27): owner-only Leave Game + Abandon
    // (Leave Game relabeled from Save & Exit by CC_GAME_NAMING).
    const [confirmingAbandon, setConfirmingAbandon] = useState(false);
    const currentGameId = lobbyData && lobbyData.current_game_id;
    const isPlaying = Boolean(
        lobbyData && lobbyData.status_id === LOBBY_STATUS_PLAYING);

    const handleSaveExit = useCallback(() => {
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.saveGame();
        // CC_FE_PAGES (2026-06-01): leaving the game lands on the Main
        // Menu. The old '/casual' route is gone.
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    }, [app]);

    const handleAbandon = useCallback(() => {
        if (!confirmingAbandon) {
            setConfirmingAbandon(true);
            return;
        }
        if (!app || !app.game || !app.game.gameApi) return;
        if (!currentGameId) return;
        app.game.gameApi.abandonGame(currentGameId);
        setConfirmingAbandon(false);
        // CC_FE_PAGES (2026-06-01): abandoning the game lands on the Main
        // Menu. The old '/casual' route is gone.
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    }, [app, confirmingAbandon, currentGameId]);

    const handleKick = useCallback((userId) => {
        if (!userId) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.kickFromGroup(userId);
    }, [app]);

    // Decorative full-viewport background (alt="" keeps it out of the
    // accessibility tree). Included on the guard returns too so the
    // loading states sit on the same art instead of bare black.
    const lobbyBackground = (
        <img
            src={LOBBY_BACKGROUND_URL}
            alt=""
            className="lobby-background-image"
        />
    );

    // ---- Render guards ----
    if (!member || !member.options) {
        return (
            <div className="lobby-root">
                {lobbyBackground}
                Loading group…
            </div>
        );
    }
    if (members && members.some((each) => !each.options)) {
        return (
            <div className="lobby-root">
                {lobbyBackground}
                Loading members…
            </div>
        );
    }
    if (launched) {
        return (
            <div className="lobby-root">
                {lobbyBackground}
                Launching…
            </div>
        );
    }

    const localMemberId = member ? member.id : null;
    const localUserId = (member && member.user) ? member.user.id : null;
    // CC_FE_PAGES §3.8 (2026-06-01): SeatRow's isOwner gate dropped the
    // secondary `localMemberId === ownerId` connection-id check — under
    // the one-UserConnection-per-user model (CC_USER_CONNECTION_STATUS)
    // a user can no longer hold multiple lobby connections, so that
    // guard defended against a class of bug that can no longer occur and
    // could misfire when a stale row won the owner `find`. isGroupLeader
    // (user-id based) is now the single owner semantic. `ownerId` was
    // used only by that dropped check.
    const isCounting = phase === 'counting';

    // CC_GAME_NAMING: lobby header game name. The group payload carries
    // current_game_id but NOT the game's name/created_by; the live game
    // payload (GameSerializer, app.game.gameData) does, and the lobby is
    // on-screen during PLAYING when that's hydrated. GameSerializer uses
    // fields='__all__', so created_by renders as the owner's integer
    // user id — which is exactly what GameNameEditable's id-based gate
    // wants (compared against this connection's localUserId, matching
    // the backend authority in rename_game.py).
    const liveGameData = (
        app && app.game && app.game.gameData
        && app.game.gameData.id === currentGameId
            ? app.game.gameData : null);
    const startEnabled = (
        isGroupLeader
        && tally.everyDecided
        && tally.allHumansReady
        && phase === 'idle');

    return (
        <div className="lobby-root">
            {lobbyBackground}
            <div className="lobby-grid">
                {currentGameId && (
                    <div className="lobby-header">
                        <GameNameEditable
                            gameId={currentGameId}
                            name={liveGameData ? liveGameData.name : null}
                            createdById={
                                liveGameData ? liveGameData.created_by : null
                            }
                            currentUserId={localUserId}
                            onRename={(newName) => {
                                if (app && app.game && app.game.gameApi) {
                                    app.game.gameApi.nameGame(
                                        currentGameId, newName);
                                }
                            }}
                        />
                    </div>
                )}
                <div className="lobby-seats">
                    {seatLayout.map((slot, seatIndex) => {
                        let isLocalUser = false;
                        if (slot.kind === 'human' && slot.occupant) {
                            // Match on the underlying User id when
                            // the occupant exposes it (lobbyData
                            // seats[] shape), else fall back to the
                            // outer id (legacy fallback path where
                            // occupant is the UserConnection record).
                            const occupantUserId = (
                                slot.occupant.user
                                && slot.occupant.user.id);
                            if (occupantUserId !== undefined
                                    && occupantUserId !== null
                                    && localUserId !== null) {
                                isLocalUser = occupantUserId === localUserId;
                            } else {
                                isLocalUser = (
                                    slot.occupant.id === localMemberId);
                            }
                        }
                        let isPlayerOne = (
                            slot.kind !== 'closed'
                            && slot.kind !== 'open'
                            && seatIndex === designatedSeat);
                        // CC_SEAT_IDENTITY_MARKS: does THIS seat's
                        // occupant own the lobby? Integer-id match on the
                        // occupant's user vs the broadcast ownerUserId
                        // (gate on ids, not strings). Distinct from
                        // `isOwner` below, which is whether the LOCAL
                        // viewer is the host (owner controls).
                        let isOwnerSeat = false;
                        if (slot.kind === 'human' && slot.occupant) {
                            const occUserId = (
                                slot.occupant.user
                                && slot.occupant.user.id);
                            if (occUserId !== undefined && occUserId !== null
                                    && ownerUserId !== undefined
                                    && ownerUserId !== null) {
                                isOwnerSeat = occUserId === ownerUserId;
                            }
                        }
                        return (
                            <SeatRow
                                key={seatIndex}
                                seatNumber={seatIndex}
                                seatState={slot.kind}
                                occupant={slot.occupant || null}
                                aiIndex={
                                    slot.kind === 'ai' ? slot.aiIndex : null
                                }
                                isOwner={isGroupLeader}
                                isOwnerSeat={isOwnerSeat}
                                isLocalUser={isLocalUser}
                                isPlayerOne={isPlayerOne}
                                onTakeSeat={takeSeat}
                                onAddAI={addAI}
                                onRemoveAI={() => {
                                    if (slot.kind === 'ai') {
                                        removeAIByIndex(slot.aiIndex);
                                    }
                                }}
                                onClose={() => closeSeat(seatIndex)}
                                onReopen={() => reopenSeat(seatIndex)}
                                onToggleReady={toggleReady}
                                onDesignatePlayerOne={
                                    () => setDesignatedSeat(seatIndex)
                                }
                                onKick={handleKick}
                            />
                        );
                    })}
                    {!isPlaying && isGroupLeader && (
                        <div className="lobby-hint">
                            This is your lobby. Fill the empty seats with
                            AIs, close any you don&apos;t need, and tune
                            the game on the right — deck size, ghosts,
                            expansions, the works. When every human seat
                            shows Ready, Start kicks off a 5-second
                            countdown. Invite friends with the share link
                            from your platform.
                        </div>
                    )}
                    {!isPlaying && !isGroupLeader && (
                        <div className="lobby-hint">
                            Take an open seat and click Ready when
                            you&apos;re set. The host owns the game
                            settings on the right and starts the game
                            once every human seat is Ready.
                        </div>
                    )}
                </div>
                <div className="lobby-config">
                    <GameConfig
                        app={app}
                        lobbyData={lobbyData}
                        isGroupLeader={isGroupLeader}
                        countdown={isCounting ? countdown : null}
                    />
                </div>
                <div className="lobby-controls">
                    <div className="lobby-controls-tally">
                        {tally.humansReady} / {tally.humans} ready
                    </div>
                    {!isCounting && isGroupLeader && (
                        <Button
                            variant="seed"
                            disabled={!startEnabled}
                            onClick={startCountdown}
                            className="lobby-start-button"
                            title={
                                startEnabled
                                    ? 'Launch the game'
                                    : 'Every seat must be decided and '
                                      + 'every human must be Ready.'
                            }
                        >
                            Start
                        </Button>
                    )}
                    {isCounting && (
                        <div className="lobby-countdown">
                            <div
                                className="lobby-countdown-label"
                                role="status"
                            >
                                Launching in {countdown === null
                                    ? COUNTDOWN_SECONDS
                                    : countdown}
                            </div>
                            <Button
                                variant="dread"
                                onClick={cancelCountdown}
                                className="lobby-cancel-button"
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                    {!isCounting && isGroupLeader && isPlaying && (
                        <>
                            <Button
                                variant="seed"
                                onClick={handleSaveExit}
                                className="lobby-leave-game"
                                title={
                                    'Leave the game. It stays saved '
                                    + 'and resumable.'
                                }
                            >
                                Leave Game
                            </Button>
                            <Button
                                variant={
                                    confirmingAbandon
                                        ? 'dread'
                                        : 'outline-dread'
                                }
                                onClick={handleAbandon}
                                className="lobby-abandon"
                                title={
                                    confirmingAbandon
                                        ? 'Click again to delete the '
                                          + 'game permanently.'
                                        : 'Delete the game permanently '
                                          + '— two clicks to confirm.'
                                }
                            >
                                {confirmingAbandon
                                    ? 'Confirm Abandon'
                                    : 'Abandon'}
                            </Button>
                        </>
                    )}
                    {!isCounting && (
                        <Button
                            variant="hex"
                            onClick={() => window.dispatchEvent(
                                new CustomEvent('quick-router-event',
                                    { detail: '/saves' }))}
                            className="lobby-saved-games"
                            title="View and resume your saved games."
                        >
                            Saved Games
                        </Button>
                    )}
                    {!isCounting && (
                        <Button
                            variant="dread"
                            onClick={handleLeaveLobby}
                            className="lobby-leave-action"
                            title={
                                'Leave the lobby. If you are the host '
                                + 'and others are present, the lobby '
                                + 'is destroyed and each peer returns to '
                                + 'the Main Menu.'
                            }
                        >
                            Leave Lobby
                        </Button>
                    )}
                </div>
            </div>
            <PeerLeftModal app={app} isGroupLeader={isGroupLeader} />
        </div>
    );
}

